import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { QueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';

import {
  SearchCityListDocument,
  SearchCityListQuery,
  SearchCityListQueryVariables,
} from '@controller/schema';
import { fetchData } from '@lib/fetcher';
import { FlowConnector } from '@modules/flow/FlowConnector';
import { getQueryName } from '@util/getQueryName';

const TITLE = 'Qeeper - Booking Flow';
const DESC =
  'On-demand property maintenance across London, Edinburgh and Brighton';
const URL = 'https://www.qeeper.co.uk/booking-flow';

const SEO_KEYWORDS =
  'On-demand property maintenance across London, Edinburgh and Brighton';

const StartFlowPage = () => {
  const router = useRouter();
  const { query, serviceName } = router.query;

  if (router.isFallback || !(query || serviceName)) {
    return <div />;
  }
  return (
    <>
      <Head>
        <meta name="keywords" content={SEO_KEYWORDS} />
        <link rel="canonical" key="canonical" href={URL} />
        <link rel="alternate" hrefLang="en" href={URL} />
      </Head>
      <NextSeo
        title={TITLE}
        description={DESC}
        canonical={URL}
        openGraph={{
          url: URL,
          title: TITLE,
          description: DESC,
        }}
      />
      <FlowConnector city={query as string} />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const queryClient = new QueryClient();
  await queryClient.prefetchQuery(
    getQueryName(SearchCityListDocument),
    fetchData<SearchCityListQuery, SearchCityListQueryVariables>(
      SearchCityListDocument,
    ),
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};

export default StartFlowPage;
